import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  keyframes,
} from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import AddCircleOutlinedIcon from '@mui/icons-material/AddCircleOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import WarningImage from '../../../assets/images/3d-icons/warning.png';
import { Product_Classification_Tag_Group_Enum } from '@kheops/graphql';
import { useEffect, useRef } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import MultiChoicesAutocomplete from '../../../common/components/MultiChoicesAutocomplete';
import { useClassifyProductTagsLazyQuery } from '../../../queries/__generated__/classifyProductTags.generated';
import { useProductClassificationGenericProductsQuery } from '../../../queries/__generated__/productClassificationGenericProducts.generated';
import { useProductClassificationTagsQuery } from '../../../queries/__generated__/productClassificationTags.generated';
import { DraftProduct } from '../state';
import { suggestionPromptInputsKey } from './suggestionPromptInputs';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

interface ProductFormVisibilityFieldsProps {
  canRefreshTagSuggestions?: boolean;
}

const TAG_GROUP_LABEL_KEY: Record<Product_Classification_Tag_Group_Enum, string> = {
  [Product_Classification_Tag_Group_Enum.Evenement]: 'discovery:events',
  [Product_Classification_Tag_Group_Enum.ProduitsDuMonde]: 'discovery:world_products',
  [Product_Classification_Tag_Group_Enum.RegimesAlimentaires]: 'discovery:diet_and_nutrition',
  [Product_Classification_Tag_Group_Enum.ValeursEngagements]: 'discovery:values_and_commitments',
  [Product_Classification_Tag_Group_Enum.MomentsConsommation]: 'discovery:consumption_moments',
};

export default function ProductFormVisibilityFields({
  canRefreshTagSuggestions = false,
}: ProductFormVisibilityFieldsProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'products', 'product-classification', 'product-classification-tags', 'discovery', 'validation']);
  const { control, getValues, setValue } = useFormContext<DraftProduct>();
  const subFamily = useWatch({ control, name: 'sub_family' });
  const genericProductCode = useWatch({ control, name: 'generic_product_code' });
  const name = useWatch({ control, name: 'name' });
  const description = useWatch({ control, name: 'description' });
  const brand = useWatch({ control, name: 'brand' });
  const labels = useWatch({ control, name: 'labels' });
  const classificationTagCodes = useWatch({ control, name: 'classification_tag_codes' }) || [];
  const classificationTagsNone = useWatch({ control, name: 'classification_tags_none' });
  const lastSuggestionInputsRef = useRef<string | null>(null);
  const [classifyTags, { data: suggestionsData, error: suggestionsError, loading: suggestionsLoading }] = useClassifyProductTagsLazyQuery();

  const { data: genericProductsData } = useProductClassificationGenericProductsQuery({
    variables: { subFamilyCode: subFamily || '' },
    skip: !subFamily,
  });
  const { data: tagsData, loading: tagsLoading } = useProductClassificationTagsQuery();

  const genericOptions = genericProductsData?.product_classification_node || [];
  const tagOptions = tagsData?.product_classification_tag || [];
  const selectedTags = classificationTagCodes.flatMap((code) => {
    const option = tagOptions.find((candidate) => candidate.code === code);

    return option ? [option] : [];
  });
  const aiSuggestionCodes = !suggestionsError && suggestionsData?.classifyProductTags?.status === 'classified'
    ? suggestionsData.classifyProductTags.tagCodes || []
    : [];
  const visibleSuggestionCodes = aiSuggestionCodes.filter((code) => !classificationTagCodes.includes(code));
  const allSuggestionsAdded = !!aiSuggestionCodes.length && !visibleSuggestionCodes.length;
  const areSuggestionsOutdated = lastSuggestionInputsRef.current !== null
    && suggestionPromptInputsKey({ name, description, brand, labels }) !== lastSuggestionInputsRef.current;
  const showSuggestions = canRefreshTagSuggestions || suggestionsLoading || !!visibleSuggestionCodes.length || allSuggestionsAdded;

  const requestSuggestions = (): void => {
    const values = getValues();

    lastSuggestionInputsRef.current = suggestionPromptInputsKey(values);
    classifyTags({
      variables: {
        name: values.name,
        subFamily: values.sub_family,
        brand: values.brand,
        description: values.description,
        labels: values.labels,
        genericProductCode: values.generic_product_code,
      },
    });
  };

  useEffect(() => {
    if (!genericProductsData) {
      return;
    }

    const code = getValues('generic_product_code');
    if (code && !genericOptions.some((option) => option.code === code)) {
      setValue('generic_product_code', null, { shouldDirty: true, shouldValidate: true });
    }
  }, [genericProductsData]);

  useEffect(() => {
    requestSuggestions();
  }, [subFamily, genericProductCode]);

  const addSuggestion = (code: string): void => {
    if (!classificationTagCodes?.includes(code)) {
      setValue('classification_tag_codes', [...classificationTagCodes, code], { shouldDirty: true });
      setValue('classification_tags_none', false, { shouldDirty: true });
    }
  };

  return (
    <>
      <Grid
        size={{ xs: 12 }}
        sx={{
          display: 'flex',
          flexDirection: {
            xs: 'column',
            sm: 'row',
          },
          gap: 3,
          alignItems: 'flex-start',
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          <Typography variant="titleMedium">
            {t('products:product_nature')}*
          </Typography>
          <Typography variant="bodySmall" color="onSurfaceVariant">
            {t('products:product_nature_description')}
          </Typography>
        </Box>
        <Box
          sx={{
            flex: 1,
            width: {
              xs: '100%',
              sm: 'auto',
            },
          }}
        >
          <Controller
            name="generic_product_code"
            control={control}
            rules={{ required: t('validation:this_field_is_required') as string }}
            render={({ field, fieldState }) => {
              const selectedOption = genericOptions.find((option) => option.code === field.value)
                ?? (field.value ? { code: field.value, parent_code: null } : null);
              const options = selectedOption && !genericOptions.some((option) => option.code === selectedOption.code)
                ? [selectedOption, ...genericOptions]
                : genericOptions;

              return (
                <>
                  <Autocomplete
                    size="small"
                    fullWidth
                    openOnFocus
                    autoHighlight
                    handleHomeEndKeys
                    blurOnSelect
                    options={options}
                    value={selectedOption}
                    onChange={(_, option) => field.onChange(option?.code ?? null)}
                    getOptionLabel={(option) => t(`product-classification:${option.code}`)}
                    isOptionEqualToValue={(option, value) => option.code === value.code}
                    noOptionsText={t('common:no_result')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name={field.name}
                        placeholder={t('products:product_nature_placeholder')}
                        error={!!fieldState.error}
                        sx={{
                          '& .MuiInputBase-input::placeholder': {
                            color: 'placeholder.main',
                            opacity: 1,
                          },
                        }}
                      />
                    )}
                  />
                  {fieldState.error
                    && <FormHelperText sx={{ ml: 2 }} error>{fieldState.error.message}</FormHelperText>}
                </>
              );
            }}
          />
        </Box>
      </Grid>

      <Grid
        size={{ xs: 12 }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          <Typography variant="titleMedium">
            {t('products:classification_tags')}*
          </Typography>
          <Typography variant="bodySmall" color="onSurfaceVariant">
            {t('products:classification_tags_description')}
          </Typography>
        </Box>
        <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
          <Controller
            name="classification_tag_codes"
            control={control}
            render={({ field }) => (
              <MultiChoicesAutocomplete
                options={tagOptions}
                values={selectedTags}
                onChange={(values) => {
                  field.onChange(values.map((option) => option.code));
                  setValue('classification_tags_none', false, { shouldDirty: true });
                }}
                getOptionLabel={(option) => t(`product-classification-tags:${option.code}`)}
                isOptionEqualToValue={(option, value) => option.code === value.code}
                groupBy={(option) => t(TAG_GROUP_LABEL_KEY[option.tag_group])}
                label={t('products:classification_tags_placeholder')}
                disabled={tagsLoading || !!classificationTagsNone}
                sx={{
                  width: '100%',
                  '& .MuiInputBase-input': {
                    top: 0,
                  },
                }}
                InputProps={{
                  startAdornment: <SearchOutlinedIcon />,
                }}
              />
            )}
          />
          {showSuggestions && (
            <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0, maxWidth: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="labelMedium">
                  {t('products:classification_tags_suggestions')}
                </Typography>
                {canRefreshTagSuggestions && areSuggestionsOutdated && (
                  <IconButton
                    size="small"
                    aria-label={t('products:classification_tags_refresh')}
                    title={t('products:classification_tags_refresh')}
                    onClick={requestSuggestions}
                    disabled={suggestionsLoading}
                  >
                    <RefreshOutlinedIcon
                      sx={{ animation: suggestionsLoading ? `${spin} 1s linear infinite` : 'none' }}
                    />
                  </IconButton>
                )}
              </Box>
              {suggestionsLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
              {!suggestionsLoading && !!visibleSuggestionCodes.length && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {visibleSuggestionCodes.map((code) => {
                    const keyword = t(`product-classification-tags:${code}`);

                    return (
                      <Tooltip placement="top" key={code} title={t('products:classification_tags_add_suggestion', { keyword })}>
                        <span>
                          <Chip
                            size="small"
                            variant="outlined"
                            icon={<AddCircleOutlinedIcon />}
                            label={keyword}
                            disabled={!!classificationTagsNone}
                            onClick={() => addSuggestion(code)}
                          />
                        </span>
                      </Tooltip>
                    );
                  })}
                </Box>
              )}
              {!suggestionsLoading && allSuggestionsAdded && (
                <Typography variant="bodySmall" color="onSurfaceVariant">
                  {t('products:classification_tags_suggestions_empty')}
                </Typography>
              )}
              {canRefreshTagSuggestions && areSuggestionsOutdated && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    p: 2,
                    mt: 1,
                    mb: 2,
                    borderRadius: 4,
                    backgroundColor: 'surfaceContainerLow.main',
                    width: '100%',
                    maxWidth: 'min(100%, 432px)',
                    minWidth: 0,
                    boxSizing: 'border-box',
                  }}
                >
                  <Chip
                    variant="tag"
                    color="warningContainer"
                    icon={<img src={WarningImage} alt="" />}
                    label={t('products:classification_tags_suggestions_outdated_badge')}
                  />
                  <Typography variant="bodyMedium" sx={{ alignSelf: 'stretch' }}>
                    {t('products:classification_tags_suggestions_outdated')}
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshOutlinedIcon />}
                    onClick={requestSuggestions}
                    disabled={suggestionsLoading}
                    sx={{
                      maxWidth: '100%',
                      whiteSpace: 'normal',
                      height: 'auto',
                      maxHeight: 'none',
                    }}
                  >
                    {t('products:classification_tags_refresh')}
                  </Button>
                </Box>
              )}
            </Box>
          )}
          <Controller
            name="classification_tags_none"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                sx={{ mt: 1, mr: 0 }}
                control={(
                  <Checkbox
                    checked={!!field.value}
                    name={field.name}
                    onChange={(event) => {
                      field.onChange(event.target.checked);
                    }}
                  />
                )}
                label={t('products:classification_tags_none_option')}
              />
            )}
          />
        </Box>
      </Grid>
    </>
  );
}
